import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, FileText, Plus } from "lucide-react";

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [lang, setLang] = useState("pt");
  const [savingKey, setSavingKey] = useState(null);
  const bodyRefs = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data);
    } catch (e) { toast.error("Erro ao carregar templates"); }
  };
  useEffect(() => { load(); }, []);

  const setField = (key, field, value) =>
    setTemplates((ts) => ts.map((t) => (t.key === key ? { ...t, [field]: value } : t)));

  const save = async (t) => {
    setSavingKey(t.key);
    try {
      await api.put("/admin/email-templates", {
        key: t.key,
        pt_subject: t.pt_subject, pt_body: t.pt_body,
        en_subject: t.en_subject, en_body: t.en_body,
        br_subject: t.br_subject || "", br_body: t.br_body || "",
      });
      toast.success("Template guardado");
    } catch (e) { toast.error("Erro ao guardar"); }
    setSavingKey(null);
  };

  const reset = async (t) => {
    if (!window.confirm("Repor o template predefinido (PT e EN)? As suas alterações serão perdidas.")) return;
    try {
      await api.post(`/admin/email-templates/${t.key}/reset`);
      toast.success("Template reposto");
      load();
    } catch (e) { toast.error("Erro ao repor"); }
  };

  const insertVar = (key, token) => {
    const ref = bodyRefs.current[`${key}_${lang}`];
    const field = `${lang}_body`;
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    const cur = t[field] || "";
    if (ref && typeof ref.selectionStart === "number") {
      const s = ref.selectionStart, e = ref.selectionEnd;
      const next = cur.slice(0, s) + token + cur.slice(e);
      setField(key, field, next);
      requestAnimationFrame(() => { ref.focus(); ref.selectionStart = ref.selectionEnd = s + token.length; });
    } else {
      setField(key, field, cur + token);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-5" data-testid="email-templates-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><FileText size={18} className="text-[#005F73]" /> Conteúdo dos emails (templates)</h2>
        <div className="inline-flex rounded-lg bg-[#F1F5F9] p-0.5 text-xs font-semibold" data-testid="email-templates-lang">
          {[["pt", "Português"], ["br", "Português (BR)"], ["en", "English"]].map(([v, label]) => (
            <button key={v} onClick={() => setLang(v)} className={`px-3 py-1.5 rounded-md transition-colors ${lang === v ? "bg-white shadow-sm text-[#0B1D3A]" : "text-[#0B1D3A]/55"}`} data-testid={`email-templates-lang-${v}`}>{label}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#0B1D3A]/55">Edita o assunto e o texto de cada email. Usa as variáveis (clica para inserir no corpo) — serão substituídas pelos valores reais no envio.</p>

      {templates.map((t) => (
        <div key={t.key} className="rounded-xl border border-[#0B1D3A]/10 p-4 space-y-3" data-testid={`email-template-${t.key}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="font-fredoka font-bold text-[#0B1D3A]">{lang === "en" ? t.label_en : t.label_pt}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => reset(t)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0B1D3A]/5 text-[#0B1D3A]/70 hover:bg-[#0B1D3A]/10 text-xs font-semibold" data-testid={`email-template-reset-${t.key}`}>
                <RotateCcw size={14} /> Repor predefinido
              </button>
              <button onClick={() => save(t)} disabled={savingKey === t.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#005F73] text-white text-xs font-semibold disabled:opacity-50" data-testid={`email-template-save-${t.key}`}>
                <Save size={14} /> {savingKey === t.key ? "A guardar..." : "Guardar"}
              </button>
            </div>
          </div>

          <label className="block text-xs font-semibold text-[#0B1D3A]/70">Assunto
            <input
              value={t[`${lang}_subject`] || ""}
              onChange={(e) => setField(t.key, `${lang}_subject`, e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm"
              data-testid={`email-template-subject-${t.key}`}
            />
          </label>

          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              <span className="text-[11px] text-[#0B1D3A]/50 mr-1">Variáveis:</span>
              {t.variables.map((v) => (
                <button key={v} type="button" onClick={() => insertVar(t.key, v)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#FF6B6B]/10 text-[#FF6B6B] text-[11px] font-mono font-semibold hover:bg-[#FF6B6B]/20" data-testid={`email-template-var-${t.key}-${v.replace(/[{}]/g, "")}`}>
                  <Plus size={10} /> {v}
                </button>
              ))}
            </div>
            <textarea
              ref={(el) => { bodyRefs.current[`${t.key}_${lang}`] = el; }}
              value={t[`${lang}_body`] || ""}
              onChange={(e) => setField(t.key, `${lang}_body`, e.target.value)}
              rows={9}
              className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm font-mono leading-relaxed"
              data-testid={`email-template-body-${t.key}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
